/**
 * Hold counted quantities on the device until they upload.
 *
 * Transposed from slipQueue.js, and for the same reason: a count is two hours
 * of work in a storeroom with no signal, and a counted figure that fails to
 * upload is two hours thrown away. So the count goes into IndexedDB first and
 * the network is treated as something that might not be there.
 *
 * `stockTakeId` IS NOT OPTIONAL, and it is on the RECORD rather than held by
 * the screen — the identical hazard slipQueue.js:33-58 describes. The queue
 * outlives the screen: a counter walks the shelves offline, comes back, opens
 * a different sheet, the screen remounts and flushes, and without a take on the
 * record those figures are variances posted against the wrong count. The server
 * cannot catch it, because the counter genuinely holds `stock.count` on both.
 *
 * ONE DELIBERATE DIFFERENCE FROM THE SLIP QUEUE, and it is the whole reason
 * this is not a copy. A slip is a NEW FACT each time — a second photograph is a
 * second slip, and the queue stacks them. A count is a VALUE FOR A LINE — a
 * second count of the same line REPLACES the first, and a replay of a stale one
 * must not undo it. So:
 *
 *  - `clientRef` is minted ONCE PER COUNT WRITE, at enqueue. Minting it at
 *    flush would make every retry a new write and defeat the server-side
 *    deduplication (041's unique index) the whole queue rests on. The same
 *    ref the line already carries is a no-op server-side; that is a retry
 *    after a lost acknowledgement, not a second count.
 *  - `deviceCountedAt` is stamped from THE DEVICE CLOCK AT ENQUEUE, not at
 *    flush. It is what lets the server refuse a stale replay: the queue holds
 *    WID-01 = 8 at 14:00 and WID-01 = 6 at 14:30, the 14:00 retry flushes
 *    last, and without that stamp the sheet ends at 8. `counted_at` is now()
 *    at write time and says nothing about when the shelf was looked at.
 *    Omitting it means last-write-wins, which is the weaker contract, so it is
 *    always sent.
 *  - QUEUE-SIDE, A NEWER COUNT OF A LINE SUPERSEDES THE OLDER QUEUED ONE
 *    rather than stacking behind it. Both mechanisms are needed: the key path
 *    below makes the second write replace the first in the store, and
 *    `itemsForTake` collapses anyway, so nothing that somehow got past the
 *    store can send two POSTs for one line.
 */
const DB = 'stock-counts', STORE = 'queue';

function open() {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    // KEYED BY THE LINE, not by clientRef — the one structural difference from
    // slipQueue.js. A slip's key is its own identity because every slip is its
    // own fact; a count's key is the LINE it answers, so a recount of that line
    // overwrites the queued answer instead of joining it in the queue.
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'key' });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/**
 * The identity of a count: which sheet, which item, and — for an individual
 * item — which serial.
 *
 * A serial is part of the key because one take can hold twenty lines for one
 * item id, one per laptop, and they are twenty different answers. Pure and
 * exported so the supersede rule can be reasoned about without IndexedDB.
 */
export const lineKey = ({ stockTakeId, itemId, serialNo = null }) =>
  `${stockTakeId}|${itemId}|${serialNo ?? ''}`;

/**
 * The record a count becomes, with its ref minted and its clock stamped.
 *
 * Pure and separate from the write below ONLY so that the two things the
 * server's deduplication and stale-replay guard depend on — a ref minted once
 * per write, and a device time taken at enqueue — are testable without
 * IndexedDB. `now` is injectable for the same reason; production never passes
 * either argument.
 *
 * `countedQty` must already be a canonical quantity string: parse it with
 * quantity.js before it gets here, so a refusal reaches the counter while they
 * are still looking at the field and not three hours later as a failed queue
 * item.
 */
export function buildCount(
  { stockTakeId, itemId, serialNo = null, countedQty, note = null },
  now = () => new Date(),
) {
  if (!stockTakeId) throw new Error('a queued count must name the stock take it belongs to');
  if (!itemId) throw new Error('a queued count must name the item it counted');
  if (typeof countedQty !== 'string' || countedQty === '') {
    throw new Error('a queued count must carry a quantity as a canonical string');
  }

  return {
    key: lineKey({ stockTakeId, itemId, serialNo }),
    // Minted HERE, once, for this write. Never at flush time — that would make
    // every retry a new write and defeat the deduplication the queue rests on.
    clientRef: crypto.randomUUID(),
    stockTakeId,
    itemId,
    serialNo,
    countedQty,
    note,
    // The device's own clock, with its offset — a phone in Johannesburg stamps
    // +02:00 and rewriting that here is one more place to get it wrong.
    deviceCountedAt: now().toISOString(),
    queuedAt: Date.now(),
  };
}

/** Queue one counted quantity against the take it was counted for. */
export async function enqueueCount(count, now) {
  const record = buildCount(count, now);
  const db = await open();
  await new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    // `put` on the line key: the newer count REPLACES the older queued one.
    tx.objectStore(STORE).put(record);
    tx.oncomplete = res; tx.onerror = () => rej(tx.error);
  });
  return record;
}

export async function queued() {
  const db = await open();
  return new Promise((res, rej) => {
    const r = db.transaction(STORE).objectStore(STORE).getAll();
    r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
  });
}

/**
 * Delete this line's queued count — BUT ONLY IF IT IS STILL THE ONE NAMED.
 *
 * ⚠ THE ONE HAZARD THE TRANSPOSITION FROM slipQueue.js INTRODUCED, and it does
 *   not exist there. slipQueue keys its store by `clientRef`, which is unique
 *   per slip, so its `drop` can only ever delete the record it just sent. This
 *   store is keyed by the LINE, deliberately, so that a recount replaces the
 *   queued answer instead of stacking behind it — and that same key is what
 *   makes an unconditional delete dangerous.
 *
 *   `flushQueue` snapshots the queue, then posts. If the counter looks again
 *   and corrects the figure WHILE THAT POST IS IN FLIGHT — which is the
 *   ordinary motion, "key it, look again, correct it", and on a bad connection
 *   the window is seconds — `enqueueCount` puts the newer count at the same
 *   key. A delete by key alone would then throw away the NEWER, UNSENT count,
 *   the screen would show nothing pending, and the sheet would re-render the
 *   server's older figure. The counter's correction would be gone, silently,
 *   which is the exact outcome this whole module exists to prevent.
 *
 *   So identity travels with the delete: the record is removed only if it still
 *   carries the `clientRef` that was actually sent. Returns whether it did.
 */
export async function dropSent(key, clientRef) {
  const db = await open();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get = store.get(key);
    let dropped = false;
    get.onsuccess = () => {
      const held = get.result;
      // The whole guard. A newer count has a different ref, and survives.
      if (held && held.clientRef === clientRef) { store.delete(key); dropped = true; }
    };
    tx.oncomplete = () => res(dropped); tx.onerror = () => rej(tx.error);
  });
}

/**
 * A discarded count is the counter's deliberate choice, made from what
 * `flushQueue` marked failed — never something this module decides on its own.
 *
 * Identity-guarded for the same reason: the failed list on screen is a
 * snapshot, and by the time somebody taps Remove the counter may have recounted
 * that line. Removing the refused figure must never remove the replacement.
 */
export const discard = dropSent;

/**
 * Record that the server will never accept this count, without deleting it —
 * AND ONLY IF IT IS STILL THE ONE THAT WAS REFUSED.
 *
 * The same race as `dropSent`, with a worse ending: marking the newer record
 * failed would exclude a count nobody has even tried to send from every future
 * pass, and leave it sitting in the queue looking like the server's decision.
 */
export async function markFailed(key, clientRef, message) {
  const db = await open();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get = store.get(key);
    let marked = false;
    get.onsuccess = () => {
      const held = get.result;
      if (held && held.clientRef === clientRef) {
        store.put({ ...held, failed: true, failedReason: message });
        marked = true;
      }
    };
    tx.oncomplete = () => res(marked); tx.onerror = () => rej(tx.error);
  });
}

/**
 * The queued counts that belong to `stockTakeId` and are ready to send —
 * oldest first BY DEVICE TIME, one per line, skipping the ones already marked
 * failed.
 *
 * Pure and exported so the two rules that matter are testable without
 * IndexedDB or a fake API: the take scoping that keeps one sheet's figures off
 * another sheet, and the supersede that stops one line producing two POSTs.
 *
 * NO DEFAULTING FOR A RECORD WITH NO TAKE, and this is where the transposition
 * from slipQueue.js deliberately stops. That module treats an unlabelled slip
 * as belonging to whoever is asking, because pre-scoping records exist on real
 * phones and stranding a photograph nobody can retake is the worse outcome.
 * This module shipped scoped, so an unlabelled record cannot have been written
 * by any version of it — and "whoever is asking" is precisely how a figure
 * lands on the wrong sheet. It is excluded, silently and always.
 *
 * Order is `deviceCountedAt` and not `queuedAt`: the device clock is the one
 * the server's stale-replay guard compares against, so sending in any other
 * order would hand the server a sequence it then has to undo.
 */
export function itemsForTake(items, stockTakeId) {
  const mine = items
    .filter((item) => !item.failed)
    .filter((item) => Boolean(item.stockTakeId) && item.stockTakeId === stockTakeId);

  // ONE POST PER LINE. Two queued answers for one line means the counter
  // recounted; only the newer one is a count, the older is a figure they
  // already replaced. Sending both would make the server's stale-replay guard
  // do work it should never have been asked to do — and if the older one
  // happened to arrive last WITHOUT a device stamp, it would win.
  const newest = new Map();
  for (const item of mine) {
    const key = item.key ?? lineKey(item);
    const held = newest.get(key);
    if (!held || isNewer(item, held)) newest.set(key, item);
  }

  return [...newest.values()].sort(
    (a, b) => cmp(a.deviceCountedAt, b.deviceCountedAt) || (a.queuedAt ?? 0) - (b.queuedAt ?? 0));
}

/** The counts for this take the server has permanently refused, for the screen to show. */
export const failedForTake = (items, stockTakeId) =>
  items.filter((item) => item.failed && item.stockTakeId === stockTakeId);

/**
 * Every count for this take that has not yet been sent, failed or not, keyed by
 * line — what the counter keyed, so the sheet can show it back to them while
 * they are still offline. A count they cannot see is a count they will key
 * again.
 */
export function pendingByLine(items, stockTakeId) {
  const out = new Map();
  for (const item of items) {
    if (item.stockTakeId !== stockTakeId) continue;
    const key = item.key ?? lineKey(item);
    const held = out.get(key);
    if (!held || isNewer(item, held)) out.set(key, item);
  }
  return out;
}

// ISO-8601 with an offset does not sort lexicographically across offsets, and
// Date.parse is the only correct comparison. It produces a number for ordering
// only — nothing in this module does arithmetic on a quantity that way.
// A record with no stamp sorts first: it was written by something that did not
// send one, and last-write-wins is the weaker contract it already chose.
// NOT `Date.parse(a ?? 0)` — that coerces to the string "0", which V8 parses as
// 2000-01-01 rather than NaN, so a stampless record would sort as the year 2000
// by coincidence rather than by decision.
const stamp = (v) => (v ? Date.parse(v) : 0);
const cmp = (a, b) => stamp(a) - stamp(b);
const isNewer = (a, b) => (cmp(a.deviceCountedAt, b.deviceCountedAt) || (a.queuedAt ?? 0) - (b.queuedAt ?? 0)) > 0;

/** The IndexedDB store, as the seam flushQueue writes through. */
const idbStore = { queued, drop: dropSent, markFailed };

/**
 * Try to upload every count queued for this take. Safe to call as often as you
 * like: the server deduplicates on `clientRef`, so a retry after a lost
 * acknowledgement is not a second count — it returns the line unchanged.
 *
 * ONLY counts queued for `stockTakeId` are sent — see itemsForTake. Whatever
 * belongs to another sheet stays queued for the sheet it belongs to.
 *
 * The failure handling is slipQueue.js's, unchanged, because the reasoning is
 * unchanged: a 4xx the server will never accept must not block every good count
 * behind it, so it is marked `failed` and skipped — never deleted, so it stays
 * visible with its reason and is only ever removed by an explicit `discard`.
 * A network failure (no `.status` at all) or an auth problem (401/403) stops
 * the whole pass, since retrying the rest would fail identically.
 *
 * `COUNT_CLIENT_REF_REUSED` and a sheet that has been posted or abandoned since
 * (409 TAKE_NOT_OPEN) both land in the 4xx arm and are shown to the counter as
 * what they are. That is the honest outcome: those figures have nowhere to go,
 * and quietly dropping them would tell somebody their count went in.
 *
 * `store` is injectable only so the flush loop is testable without IndexedDB.
 */
export async function flushQueue(api, stockTakeId, store = idbStore) {
  const items = itemsForTake(await store.queued(), stockTakeId);
  let sent = 0, failed = 0;
  for (const item of items) {
    try {
      await api.post(`/api/stock-takes/${stockTakeId}/count`, {
        itemId: item.itemId,
        serialNo: item.serialNo ?? null,
        countedQty: item.countedQty,
        clientRef: item.clientRef,
        deviceCountedAt: item.deviceCountedAt,
        note: item.note ?? null,
      });
      // BY IDENTITY, not by key — see dropSent. A recount enqueued while
      // this POST was in flight sits at the same key and must survive it.
      await store.drop(item.key, item.clientRef);
      sent += 1;
    } catch (err) {
      const status = err?.status;
      // No status at all: fetch itself failed — genuinely offline, which on a
      // count is the expected state rather than the exceptional one. Stop and
      // leave everything, including this item, for the next attempt.
      if (status === undefined) break;
      // The session or the permission needs attention, not this count.
      if (status === 401 || status === 403) break;
      // The server will NEVER accept this count. Skip it, marked not deleted,
      // so every good count behind it still gets tried this pass.
      if (status >= 400 && status < 500) {
        await store.markFailed(item.key, item.clientRef, err.message ?? 'rejected by the server');
        failed += 1;
        continue;
      }
      break;   // 5xx — the server is unwell. Try again later.
    }
  }
  return { sent, failed };
}
